import { useEffect, useRef } from 'react';
import { Alert } from '@interstate/components/Alert';
import { AuxButton } from '@interstate/components/Button';
import * as deskingSelectors from '@makemydeal/dr-dash-store/selectors/deskingSelectors';
import { useDispatch, useSelector } from 'react-redux';

export const DeskingAlerts = ({ sx: sxOverrides = {} }) => {
    const dispatch = useDispatch();
    const alerts = useSelector(deskingSelectors.getDeskingAlerts);
    const activeTimersRef = useRef<Set<string>>(new Set());

    // This is for alerts that should auto-dismiss after an elapsed time. The Interstate Alert has an option to hide itself after
    // a few seconds, but that has no effect on redux state. That's what `clearAction` and `autoDismissMs` are for.
    useEffect(() => {
        const timers: ReturnType<typeof setTimeout>[] = [];

        alerts.forEach((alert) => {
            if (alert.clearAction && alert.autoDismissMs) {
                const key = `${alert.type}-${alert.message}`;
                if (!activeTimersRef.current.has(key)) {
                    activeTimersRef.current.add(key);
                    timers.push(
                        setTimeout(() => {
                            activeTimersRef.current.delete(key);
                            dispatch(alert.clearAction);
                        }, alert.autoDismissMs)
                    );
                }
            }
        });

        return () => {
            timers.forEach(clearTimeout);
        };
    }, [alerts, dispatch]);

    if (alerts.length === 0) return null;

    return (
        <>
            {alerts.map((alert, alertIndex) => (
                <Alert
                    type={alert.type}
                    key={`alert-${alertIndex}`}
                    role=""
                    data-testid={`desking-alert-${alertIndex}`}
                    sx={{ ...sxOverrides }}
                    title={alert.title}
                >
                    {alert.message}
                    {alert.ctaAction != null && (
                        <AuxButton
                            data-testid="cta-button"
                            /* istanbul ignore next - CTA click handler tested through integration */
                            onClick={() => {
                                dispatch(alert.ctaAction);
                            }}
                        >
                            {alert.cta}
                        </AuxButton>
                    )}
                </Alert>
            ))}
        </>
    );
};
